import React, { type ReactNode, useMemo } from 'react';
import { graphql, fetchQuery, useRelayEnvironment } from 'react-relay';
import { OPERAND_TYPE_VALUE } from '@atlaskit/jql-ast';
import { Text } from '@atlaskit/primitives/compiled';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl, type IntlShape } from '@atlassian/jira-intl';
import type { FieldOption } from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { createGenericDebouncableDataLoader } from '@atlassian/jira-jql-builder-basic-picker/src/common/utils/debouncer.tsx';
import { getSecondaryLabelWithIcon } from '@atlassian/jira-jql-builder-basic-picker/src/common/utils/get-secondary-label-with-icon.tsx';
import { FieldTypeIcon } from '@atlassian/jira-project-settings-apps-common/src/icon-map.tsx';
import type {
	main_IssueTypeJqlBuilderBasicFieldsQuery,
	main_IssueTypeJqlBuilderBasicFieldsQuery$data as issueTypePropsResolver$data,
	JiraJqlViewContext,
	JiraJqlScopeInput,
} from '@atlassian/jira-relay/src/__generated__/main_IssueTypeJqlBuilderBasicFieldsQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { PACKAGE_NAME, TEAM_NAME, EPIC_LINK } from '../../common/constants.tsx';
import { generateOptions } from '../../common/utils.tsx';
import { mapOperators } from '../../utils/operators/map-operators.tsx';
import messages from './messages.tsx';

const WHERE_CLAUSE = 'WHERE';
// suppressed to allow migration to typescript-eslint v8 - this is a new error reported with v8 and is only used as a type
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ORDER_BY_CLAUSE = 'ORDER_BY';

export type FieldNode = NonNullable<
	NonNullable<
		NonNullable<
			Extract<
				NonNullable<
					NonNullable<NonNullable<issueTypePropsResolver$data['jira']>['jqlBuilder']>['fields']
				>,
				{ __typename: 'JiraJqlFieldConnection' }
			>['edges']
		>[number]
	>['node']
>;

/**
 * Converts a field node from Hydration data to a FieldOption for use in a Picker select dropdown
 */
export const getOption = (
	node: FieldNode | undefined | null,
	itemType: 'value',
	intl: IntlShape,
): FieldOption | undefined => {
	if (!node) {
		return undefined;
	}
	const {
		displayName,
		jqlTerm,
		searchTemplate,
		type,
		operators,
		description,
		fieldType,
		shouldShowInContext,
	} = node;

	// Search template & type are required; description can be null but not undefined
	if (
		searchTemplate == null ||
		searchTemplate.key == null ||
		type == null ||
		description === undefined
	) {
		fireErrorAnalytics({
			meta: {
				id: 'getOption',
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			error: new Error('Field node missing required data'),
			attributes: {
				searchTemplate:
					searchTemplate == null
						? 'missing'
						: {
								key: searchTemplate.key,
							},
				fieldType: type == null ? 'missing' : type,
				description: description === undefined ? 'missing' : 'present',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
		return undefined;
	}

	const isDisabledEpicLinkField = searchTemplate?.key === EPIC_LINK.searchTemplate;

	const fieldTypeDisplayName = fieldType?.displayName ?? null;

	const icon = type ? <FieldTypeIcon type={type} size="small" /> : undefined;
	const fieldLabel = displayName != null ? displayName : jqlTerm;

	let isOnlyUnselectable = false;

	let disabledMessage = isDisabledEpicLinkField
		? intl.formatMessage(messages.deprecatedEpicMessage, {
				strong: (chunks: ReactNode[]) => (
					<Text as="strong" color="inherit" size="UNSAFE_small">
						{chunks}
					</Text>
				),
			})
		: undefined;

	if (shouldShowInContext === false) {
		isOnlyUnselectable = true;
		disabledMessage = intl.formatMessage(
			expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
				? messages.notInContextFieldMessageIssueTermRefresh
				: messages.notInContextFieldMessage,
		);
	}

	if (isDisabledEpicLinkField) {
		isOnlyUnselectable = true;
		disabledMessage = intl.formatMessage(messages.deprecatedEpicMessage, {
			strong: (chunks: ReactNode[]) => (
				<Text as="strong" color="inherit" size="UNSAFE_small">
					{chunks}
				</Text>
			),
		});
	}

	return {
		optionType: 'field',
		label: fieldLabel,
		value: jqlTerm.toLowerCase(),
		searchTemplate: searchTemplate?.key,
		fieldType: type,
		fieldDescription: description,
		isOnlyUnselectable,
		secondaryLabel:
			fieldTypeDisplayName && icon
				? getSecondaryLabelWithIcon(icon, fieldTypeDisplayName)
				: undefined,
		fieldTypeDisplayName,
		itemType,
		disabledMessage,
		operators: operators?.map(mapOperators),
	};
};

export const useFields = ({
	jql = '',
	excludeFields = [],
	forClause = WHERE_CLAUSE,
	viewContext = null,
	scope = null,
}: {
	jql?: string;
	excludeFields?: string[];
	/**
	 * Instructs the backend to return fields for a WHERE clause, ORDER_BY clause or both.
	 *
	 * For context, a WHERE clause is anything in a JQL that is in front of the ORDER BY operand
	 * and ORDER_BY clause is anthing on the right-hand-side of an ORDER BY operand. In the JQL
	 * `project = HSP Order By created ASC`:
	 * - The WHERE clause is made up of `project = HSP`
	 * - The ORDER_BY clause is made up of `created ASC`
	 *
	 * How it affects the query response
	 * Setting this variable will affect the jqlTerm that is returned in the response.
	 * For example, if two custom fields called `Custom Date Time` exist on an instance, setting this
	 * variable to `WHERE` would return a single field with jqlTerm: `Custom Date Time[Time stamp]`
	 * But setting this variable to `ORDER_BY` would instead yield 2 fields, each with their unique
	 * `CF[]` identifier as the jqlTerm. This is because Order By is not compatible with collapsed fields
	 */
	forClause?: typeof WHERE_CLAUSE | typeof ORDER_BY_CLAUSE;
	/**
	 * Instructs the backend to return fields corresponds to the view
	 */
	viewContext?: JiraJqlViewContext | null;
	/**
	 * Optional scope that allows consumers to customise fields returned. For example, some fields could
	 * be filtered out when a boardId scope is provided.
	 */
	scope?: JiraJqlScopeInput | null;
}) => {
	const cloudId = useCloudId();
	const environment = useRelayEnvironment();
	const intl = useIntl();

	return useMemo(
		() =>
			createGenericDebouncableDataLoader(async (search, renderInitialValues, endCursor) => {
				try {
					const data = await fetchQuery<main_IssueTypeJqlBuilderBasicFieldsQuery>(
						environment,
						graphql`
							query main_IssueTypeJqlBuilderBasicFieldsQuery(
								$cloudId: ID!
								$first: Int = 10
								$searchString: String!
								$after: String
								$excludeFields: [String!]
								$forClause: JiraJqlClauseType
								$jqlContext: String
								$viewContext: JiraJqlViewContext
								$scope: JiraJqlScopeInput
							) {
								jira @required(action: THROW) {
									jqlBuilder(cloudId: $cloudId) {
										fields(
											first: $first
											searchString: $searchString
											after: $after
											excludeFields: $excludeFields
											forClause: $forClause
											jqlContext: $jqlContext
											viewContext: $viewContext
											scope: $scope
										) {
											... on JiraJqlFieldConnection {
												__typename
												totalCount
												pageInfo {
													endCursor
												}
												edges {
													node {
														jqlTerm
														displayName
														searchTemplate {
															key
														}
														type
														description
														operators
														fieldType {
															displayName
														}
														shouldShowInContext
													}
												}
											}
											... on QueryError {
												__typename
											}
										}
									}
								}
							}
						`,
						{
							cloudId,
							first: 10,
							searchString: search,
							after: typeof endCursor === 'string' ? endCursor : undefined,
							excludeFields,
							forClause,
							jqlContext: jql,
							viewContext,
							scope,
						},
						{ fetchPolicy: 'store-or-network' },
					).toPromise();

					if (data?.jira?.jqlBuilder?.fields?.__typename === 'JiraJqlFieldConnection') {
						if (!data?.jira?.jqlBuilder?.fields?.edges) {
							return generateOptions([]);
						}

						const {
							edges: fields,
							totalCount,
							pageInfo: { endCursor: newEndCursor },
						} = data.jira.jqlBuilder.fields;

						return generateOptions(
							fields.reduce<Array<FieldOption>>((options, edge) => {
								const option = getOption(edge?.node, OPERAND_TYPE_VALUE, intl);
								if (option) {
									options.push(option);
								}

								return options;
							}, []),
							totalCount,
							newEndCursor || undefined,
						);
					}
					return { options: [], totalCount: 0 };
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireErrorAnalytics({
						meta: {
							id: 'fetchMoreFieldsOptions',
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});

					return {
						options: [
							{
								optionType: 'error',
								error,
								onRetry: () => undefined,
							},
						],
						totalCount: 0,
					};
				}
			}),
		[cloudId, environment, excludeFields, forClause, intl, jql, viewContext, scope],
	);
};
